import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { bulkImportAPI } from '../lib/api';
import { Upload, FileText, Loader2, CheckCircle2, XCircle, AlertTriangle, ArrowLeft, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

export default function AdminBulkImportPage() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [textInput, setTextInput] = useState('');
  const [csvData, setCsvData] = useState('');
  const [fileName, setFileName] = useState('');
  const [defaults, setDefaults] = useState({
    price: '5000', category: 'general', is_auction: false, auction_days: '30', min_bid: '100'
  });

  const [preview, setPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [activeTab, setActiveTab] = useState('text');

  React.useEffect(() => {
    if (!authLoading && (!user || !isAdmin)) navigate('/login');
  }, [user, isAdmin, authLoading, navigate]);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.txt')) {
      toast.error('Please upload a .csv or .txt file');
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const content = ev.target?.result;
      if (file.name.endsWith('.csv')) {
        setCsvData(content);
        setActiveTab('csv');
      } else {
        setTextInput(content);
        setActiveTab('text');
      }
    };
    reader.readAsText(file);
  };

  const handlePreview = async () => {
    const data = activeTab === 'csv'
      ? { csv_data: csvData }
      : { text_data: textInput };

    if (!data.csv_data && !data.text_data) {
      toast.error('Enter domains or upload a file first');
      return;
    }

    setLoading(true);
    try {
      const res = await bulkImportAPI.preview(data);
      setPreview(res.data);
      setImportResult(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    }
    setLoading(false);
  };

  const handleImport = async () => {
    const data = activeTab === 'csv'
      ? { csv_data: csvData, defaults }
      : { text_data: textInput, defaults: {
          ...defaults,
          price: parseFloat(defaults.price),
          auction_days: parseInt(defaults.auction_days),
          min_bid: parseFloat(defaults.min_bid),
        }};

    setImporting(true);
    try {
      const res = await bulkImportAPI.import(data);
      setImportResult(res.data);
      setPreview(null);
      toast.success(`Imported ${res.data.total_imported} domains!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    }
    setImporting(false);
  };

  const downloadTemplate = () => {
    const csv = 'domain,price,category,description,is_auction\nexample.com,5000,tech,A great domain,false\nmysite.io,3000,business,Business domain,true';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'domain_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (authLoading) return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-[#00E5FF]" /></div>;

  return (
    <div className="px-6 md:px-12 max-w-7xl mx-auto py-12 md:py-20" data-testid="bulk-import-page">
      <div className="flex items-center gap-3 mb-8">
        <Button variant="ghost" onClick={() => navigate('/admin')} className="text-zinc-400 hover:text-white" data-testid="back-to-admin">
          <ArrowLeft className="h-4 w-4 mr-1" /> Admin
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-8">
        <Upload className="h-6 w-6 text-[#00E5FF]" />
        <h1 className="text-4xl font-bold text-white font-['Outfit'] tracking-tight">Bulk Domain Import</h1>
      </div>

      {/* Input Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="bg-[#0A0A0A] border border-white/5 p-1 rounded-sm">
              <TabsTrigger value="text" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black" data-testid="tab-text">
                <FileText className="h-3 w-3 mr-1.5" /> Text Input
              </TabsTrigger>
              <TabsTrigger value="csv" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black" data-testid="tab-csv">
                <Upload className="h-3 w-3 mr-1.5" /> CSV Upload
              </TabsTrigger>
            </TabsList>

            <TabsContent value="text">
              <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-6">
                <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-2 block">Enter domains (comma, semicolon, or newline separated)</Label>
                <Textarea
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  rows={8}
                  placeholder="example.com, mysite.io, domain.net&#10;anotherdomain.com&#10;coolname.ai"
                  className="bg-[#050505] border-zinc-800 text-white rounded-sm resize-none font-mono text-sm"
                  data-testid="text-input"
                />
                <p className="text-xs text-zinc-600 mt-2">Supports: comma, semicolon, or newline separated domain names</p>
              </div>
            </TabsContent>

            <TabsContent value="csv">
              <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-6 space-y-4">
                <div className="flex items-center gap-4">
                  <input ref={fileInputRef} type="file" accept=".csv,.txt" onChange={handleFileUpload} className="hidden" />
                  <Button onClick={() => fileInputRef.current?.click()} variant="outline" className="border-zinc-800 text-zinc-400 hover:border-[#00E5FF] hover:text-[#00E5FF] rounded-sm" data-testid="upload-file-btn">
                    <Upload className="h-4 w-4 mr-2" /> Upload File
                  </Button>
                  <Button onClick={downloadTemplate} variant="outline" className="border-zinc-800 text-zinc-400 hover:border-green-500 hover:text-green-400 rounded-sm" data-testid="download-template-btn">
                    <Download className="h-4 w-4 mr-2" /> Download Template
                  </Button>
                  {fileName && <span className="text-sm text-[#00E5FF]">{fileName}</span>}
                </div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-2 block">Or paste CSV content directly</Label>
                <Textarea
                  value={csvData}
                  onChange={(e) => setCsvData(e.target.value)}
                  rows={8}
                  placeholder="domain,price,category,description,is_auction&#10;example.com,5000,tech,A premium domain,false&#10;mysite.io,3000,business,,true"
                  className="bg-[#050505] border-zinc-800 text-white rounded-sm resize-none font-mono text-sm"
                  data-testid="csv-input"
                />
                <p className="text-xs text-zinc-600">CSV headers: domain, price, category, description, is_auction</p>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Defaults Panel */}
        <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-6 space-y-4">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider">Default Settings</h3>
          <p className="text-xs text-zinc-600">Applied when metadata is not provided per domain</p>

          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Default Price ($)</Label>
            <Input value={defaults.price} onChange={(e) => setDefaults(p => ({ ...p, price: e.target.value }))}
              type="number" className="mt-1 bg-[#050505] border-zinc-800 text-white rounded-sm" data-testid="default-price" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Default Category</Label>
            <Select value={defaults.category} onValueChange={(v) => setDefaults(p => ({ ...p, category: v }))}>
              <SelectTrigger className="mt-1 bg-[#050505] border-zinc-800 text-white rounded-sm" data-testid="default-category"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                {['general', 'tech', 'business', 'crypto', 'finance', 'health', 'media', 'gaming', 'ai', 'premium', 'brandable', 'short', 'keyword'].map(c => (
                  <SelectItem key={c} value={c} className="text-zinc-300 capitalize">{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
              <input type="checkbox" checked={defaults.is_auction} onChange={(e) => setDefaults(p => ({ ...p, is_auction: e.target.checked }))} className="accent-[#00E5FF]" data-testid="default-auction" />
              List as Auction
            </label>
          </div>
          {defaults.is_auction && (
            <>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Auction Duration (days)</Label>
                <Input value={defaults.auction_days} onChange={(e) => setDefaults(p => ({ ...p, auction_days: e.target.value }))}
                  type="number" className="mt-1 bg-[#050505] border-zinc-800 text-white rounded-sm" data-testid="default-auction-days" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Min Bid ($)</Label>
                <Input value={defaults.min_bid} onChange={(e) => setDefaults(p => ({ ...p, min_bid: e.target.value }))}
                  type="number" className="mt-1 bg-[#050505] border-zinc-800 text-white rounded-sm" data-testid="default-min-bid" />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 mb-8">
        <Button onClick={handlePreview} disabled={loading} className="bg-zinc-800 text-white hover:bg-zinc-700 rounded-sm" data-testid="preview-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Preview Import
        </Button>
        <Button onClick={handleImport} disabled={importing || (!textInput && !csvData)} className="bg-[#00E5FF] text-black font-bold hover:bg-white rounded-sm" data-testid="import-btn">
          {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
          Import Domains
        </Button>
      </div>

      {/* Preview Results */}
      {preview && (
        <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-6 mb-8" data-testid="preview-results">
          <h3 className="text-lg font-bold text-white font-['Outfit'] mb-4">Preview Results</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-3 bg-[#050505] border border-white/5 rounded-sm">
              <div className="text-xs text-zinc-600 uppercase tracking-wider">Total Valid</div>
              <div className="text-xl font-bold text-white">{preview.total_valid}</div>
            </div>
            <div className="p-3 bg-green-500/5 border border-green-500/20 rounded-sm">
              <div className="text-xs text-green-400 uppercase tracking-wider">New</div>
              <div className="text-xl font-bold text-green-400">{preview.total_new}</div>
            </div>
            <div className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-sm">
              <div className="text-xs text-amber-400 uppercase tracking-wider">Already Exist</div>
              <div className="text-xl font-bold text-amber-400">{preview.total_existing}</div>
            </div>
            <div className="p-3 bg-red-500/5 border border-red-500/20 rounded-sm">
              <div className="text-xs text-red-400 uppercase tracking-wider">Errors</div>
              <div className="text-xl font-bold text-red-400">{preview.total_errors}</div>
            </div>
          </div>

          {preview.domains?.length > 0 && (
            <div className="max-h-80 overflow-y-auto border border-white/5 rounded-sm">
              <table className="w-full">
                <thead className="sticky top-0 bg-[#0A0A0A]"><tr className="border-b border-white/5">
                  <th className="text-left px-4 py-2 text-[10px] text-zinc-600 uppercase tracking-wider">Domain</th>
                  <th className="text-left px-4 py-2 text-[10px] text-zinc-600 uppercase tracking-wider">TLD</th>
                  <th className="text-left px-4 py-2 text-[10px] text-zinc-600 uppercase tracking-wider">Price</th>
                  <th className="text-left px-4 py-2 text-[10px] text-zinc-600 uppercase tracking-wider">Category</th>
                  <th className="text-left px-4 py-2 text-[10px] text-zinc-600 uppercase tracking-wider">Status</th>
                </tr></thead>
                <tbody>
                  {preview.domains.map((d, i) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-2 text-sm text-white font-medium">{d.full_name}</td>
                      <td className="px-4 py-2 text-xs text-zinc-500">{d.tld}</td>
                      <td className="px-4 py-2 text-sm text-zinc-400">{d.price ? `$${d.price.toLocaleString()}` : 'Default'}</td>
                      <td className="px-4 py-2 text-xs text-zinc-500 uppercase">{d.category || 'Default'}</td>
                      <td className="px-4 py-2">
                        {d.exists ? (
                          <span className="text-xs px-2 py-0.5 rounded-sm bg-amber-500/10 text-amber-400">Exists</span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded-sm bg-green-500/10 text-green-400">New</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {preview.errors?.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-bold text-red-400 mb-2 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Errors</h4>
              <div className="space-y-1">
                {preview.errors.map((e, i) => (
                  <div key={i} className="text-xs text-red-300 bg-red-500/5 px-3 py-1.5 rounded-sm">
                    {e.row ? `Row ${e.row}: ` : ''}{e.domain ? `"${e.domain}" - ` : ''}{e.error}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Import Results */}
      {importResult && (
        <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-6" data-testid="import-results">
          <h3 className="text-lg font-bold text-white font-['Outfit'] mb-4">Import Complete</h3>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-green-500/5 border border-green-500/20 rounded-sm">
              <div className="flex items-center gap-2 mb-1"><CheckCircle2 className="h-4 w-4 text-green-400" /><span className="text-sm font-bold text-green-400">Imported</span></div>
              <div className="text-2xl font-bold text-white font-['Outfit']">{importResult.total_imported}</div>
            </div>
            <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-sm">
              <div className="flex items-center gap-2 mb-1"><AlertTriangle className="h-4 w-4 text-amber-400" /><span className="text-sm font-bold text-amber-400">Skipped</span></div>
              <div className="text-2xl font-bold text-white font-['Outfit']">{importResult.total_skipped}</div>
            </div>
            <div className="p-4 bg-red-500/5 border border-red-500/20 rounded-sm">
              <div className="flex items-center gap-2 mb-1"><XCircle className="h-4 w-4 text-red-400" /><span className="text-sm font-bold text-red-400">Errors</span></div>
              <div className="text-2xl font-bold text-white font-['Outfit']">{importResult.total_errors}</div>
            </div>
          </div>

          {importResult.imported?.length > 0 && (
            <div className="max-h-64 overflow-y-auto border border-white/5 rounded-sm">
              <table className="w-full">
                <thead className="sticky top-0 bg-[#0A0A0A]"><tr className="border-b border-white/5">
                  <th className="text-left px-4 py-2 text-[10px] text-zinc-600 uppercase tracking-wider">Domain</th>
                  <th className="text-right px-4 py-2 text-[10px] text-zinc-600 uppercase tracking-wider">Price</th>
                  <th className="text-right px-4 py-2 text-[10px] text-zinc-600 uppercase tracking-wider">Type</th>
                </tr></thead>
                <tbody>
                  {importResult.imported.map((d, i) => (
                    <tr key={i} className="border-b border-white/5">
                      <td className="px-4 py-2 text-sm text-white">{d.domain}</td>
                      <td className="px-4 py-2 text-sm text-[#00E5FF] font-bold text-right">${d.price?.toLocaleString()}</td>
                      <td className="px-4 py-2 text-right">
                        <span className={`text-xs px-2 py-0.5 rounded-sm ${d.is_auction ? 'bg-amber-500/10 text-amber-400' : 'bg-[#00E5FF]/10 text-[#00E5FF]'}`}>
                          {d.is_auction ? 'Auction' : 'Buy Now'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
